/**
 * Paramètres de l'entreprise.
 *
 * Ces champs finissent en pied de devis et de facture : le SIRET, la TVA
 * intracommunautaire et l'IBAN y sont des mentions légales, pas de la
 * décoration. D'où une validation stricte plutôt qu'un simple champ texte.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";

const optional = z
  .string()
  .trim()
  .min(1)
  .nullish()
  .or(z.literal("").transform(() => null));

export const tenantSettingsInput = z.object({
  confirmationId: z.string().optional(),

  name: z.string().trim().min(2, "Le nom commercial est requis"),
  legalName: optional,
  /** SARL, SAS, EURL, entreprise individuelle… : la déclaration de sous-traitance la demande. */
  legalForm: optional,
  /** « RCS Nantes 853 322 915 », ou le répertoire des métiers. */
  rcs: optional,

  siret: z
    .string()
    .regex(/^\d{14}$/, "Le SIRET doit faire 14 chiffres")
    .nullish()
    .or(z.literal("").transform(() => null)),

  tvaIntra: z
    .string()
    .trim()
    .regex(/^FR[0-9A-Z]{2}\d{9}$/i, "Format attendu : FR + clé + SIREN")
    .nullish()
    .or(z.literal("").transform(() => null)),

  codeApe: z
    .string()
    .trim()
    .regex(/^\d{4}[A-Z]$/i, "Format attendu : 4 chiffres et une lettre")
    .nullish()
    .or(z.literal("").transform(() => null)),

  phone: optional,
  /**
   * Obligatoire : les devis, factures, relances et commandes partent de
   * `no-reply@dker.pro` au nom de l'entreprise, avec cette adresse en
   * `reply_to`. Sans elle, le client qui répond écrit à `no-reply` et sa
   * réponse se perd.
   */
  email: z
    .string({ required_error: "L’e-mail de l’entreprise est requis" })
    .trim()
    .min(1, "L’e-mail de l’entreprise est requis : les réponses de vos clients y arrivent")
    .email("Adresse e-mail invalide"),
  website: optional,

  addressLine1: optional,
  addressLine2: optional,
  postalCode: z
    .string()
    .regex(/^\d{5}$/, "Le code postal doit faire 5 chiffres")
    .nullish()
    .or(z.literal("").transform(() => null)),
  city: optional,

  /** Espaces tolérés à la saisie, retirés à l'enregistrement. */
  iban: z
    .string()
    .transform((v) => v.replace(/\s/g, "").toUpperCase())
    .refine(
      (v) => v === "" || /^FR\d{2}[0-9A-Z]{23}$/.test(v),
      "IBAN français attendu : FR suivi de 25 caractères",
    )
    .transform((v) => (v === "" ? null : v))
    .nullish(),
});

export type TenantSettingsInput = z.infer<typeof tenantSettingsInput>;
export type TenantSettingsFormInput = z.input<typeof tenantSettingsInput>;

export interface TenantSettings {
  id: string;
  name: string;
  legalName: string | null;
  legalForm: string | null;
  rcs: string | null;
  siret: string | null;
  tvaIntra: string | null;
  codeApe: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
  iban: string | null;
}

export const myPhoneInput = z.object({
  confirmationId: z.string().optional(),
  /** Vide efface le numero : on part d'une entreprise, on reprend son
   *  telephone avec soi. */
  phone: z.string().trim().max(20, "Numero trop long").default(""),
});

export type MyPhoneInput = z.infer<typeof myPhoneInput>;

export interface TenantRepository {
  update(
    ctx: ExecutionContext,
    patch: Omit<TenantSettingsInput, "confirmationId">,
  ): Promise<TenantSettings>;

  /**
   * Le telephone direct de l'appelant.
   *
   * Passe par `set_my_phone()` en base : depuis le 11 septembre `public.users`
   * n'accepte plus aucune ecriture directe, parce que la colonne `role` y
   * vivait a cote et se laissait reecrire par n'importe quel membre du tenant.
   */
  setMyPhone(ctx: ExecutionContext, phone: string): Promise<void>;
}

export function createTenantServices(repo: TenantRepository) {
  const enregistrerParametres = defineService<TenantSettingsInput, TenantSettings>({
    name: "enregistrer_parametres_entreprise",
    level: ActionLevel.ReversibleWrite,
    // Réversible, mais ces champs engagent l'entreprise sur chaque document
    // émis : ce n'est pas au conducteur de travaux d'y toucher.
    allowedRoles: ["patron"],
    handler: (ctx, input) => {
      const { confirmationId: _ignored, ...patch } = input;
      return repo.update(ctx, patch);
    },
  });

  /**
   * Son propre numero, que tout salarie peut poser.
   *
   * Aucun `allowedRoles`, et c'est le point : un conducteur qu'un ouvrier doit
   * pouvoir joindre depuis le chantier a besoin d'ecrire son numero lui-meme.
   * La fonction en base n'accepte de toute facon que la ligne de l'appelant.
   */
  const definirMonTelephone = defineService<MyPhoneInput, void>({
    name: "definir_mon_telephone",
    level: ActionLevel.ReversibleWrite,
    handler: (ctx, input) => repo.setMyPhone(ctx, input.phone),
  });

  return { enregistrerParametres, definirMonTelephone };
}

export type TenantServices = ReturnType<typeof createTenantServices>;
